<template>
  <DroppableFileSelector
    class="app-screenshots-selector"
    multiple
    v-bind="$attrs"
    :accept="accept"
    :disabled="disabled"
    @change="handleFileSelect"
  >
    <template v-if="imagesModel.length" v-slot="{ openFileSelector }">
      <Draggable
        v-model="imagesModel"
        v-bind="dragOptions"
        :disabled="!canChangeOrder"
        class="app-screenshots-selector__list"
        @update="emitNewOrder"
      >
        <div
          v-for="image in imagesModel"
          :key="image[keyProp]"
          class="app-screenshots-selector__list-item"
        >
          <DeleteWithConfirm
            class="app-screenshots-selector__delete-button"
            :delete-function="() => emitDeleteImage(image[keyProp])"
          />

          <img :src="image.url" class="app-screenshots-selector__image" />
        </div>

        <div slot="header" class="app-screenshots-selector__add-section">
          <ElButton
            class="app-screenshots-selector_add-button"
            icon="el-icon-circle-plus-outline"
            type="text"
            @click="openFileSelector"
          />
        </div>
      </Draggable>
    </template>
  </DroppableFileSelector>
</template>

<script>
import Draggable from 'vuedraggable';
import DroppableFileSelector from '@@/common/file-selectors/DroppableFileSelector';
import { isRTL } from '@/utils/layout';
import DeleteWithConfirm from '@@/common/action-buttons/DeleteWithConfirm';

export default {
  name: 'AppScreenshotsSelector',
  components: {
    DroppableFileSelector,
    Draggable,
    DeleteWithConfirm,
  },

  props: {
    accept: {
      type: String,
      default: '.png,.jpg,.jpeg',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    canChangeOrder: {
      type: Boolean,
      default: false,
    },
    images: {
      type: Array,
      default: () => [],
    },
    keyProp: {
      type: String,
      default: 'id',
    },
  },

  data() {
    return {
      imagesModel: [],
    };
  },

  computed: {
    dragOptions() {
      return {
        draggable: '.app-screenshots-selector__list-item',
        delay: 0,
        animation: 250,
        scroll: true,
        scrollSensitivity: 250,
        scrollSpeed: 50,
        direction: 'horizontal',
        forceFallback: true,
        ghostClass: 'app-screenshots-selector__ghost-item',
        dragClass: 'app-screenshots-selector__dragging-item',
      };
    },
  },

  watch: {
    images: {
      immediate: true,
      handler(images) {
        this.imagesModel = this.getCorrectOrderForDirection(images);
      },
    },
  },

  methods: {
    emitNewOrder() {
      const finalImages = this.getCorrectOrderForDirection(this.imagesModel);
      this.$emit('change', finalImages);
    },
    emitDeleteImage(id) {
      this.$emit('delete', id);
      return Promise.resolve();
    },
    handleFileSelect(images = []) {
      if (images.length) {
        this.$emit('file-select', images);
      }
    },
    getCorrectOrderForDirection(images) {
      // because Sortable.js (vuedraggable) doesn't support rtl
      if (isRTL()) return images.slice().reverse();
      return images.slice();
    },
  },
};
</script>

<style lang="scss">
.app-screenshots-selector {
  $block: &;
  $gutter: $global-margin / 2;

  &__list {
    display: flex;
    width: 100%;
    overflow-y: hidden;
    overflow-x: auto;
    vertical-align: top;
    padding-bottom: 5px;
    user-select: none;

    @include rtl() {
      flex-direction: row-reverse;
    }

    &::-webkit-scrollbar {
      background: #000;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #eee;
    }

    &::-webkit-scrollbar-thumb {
      background: #ddd;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #ccc;
    }
  }

  &__add-section {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    position: relative;
    margin-left: auto;
    width: rem-calc(200px);
    height: rem-calc(350px);

    .el-icon-circle-plus-outline {
      font-size: 4rem;
      color: $lb-dark-gray;

      &:hover {
        color: $lb-color-primary;
      }
    }
  }

  &__list-item {
    flex-shrink: 0;
    height: rem-calc(350px);
    position: relative;
    margin-left: $gutter;
    margin-right: $gutter;
    vertical-align: bottom;

    cursor: move;
  }

  &__image {
    width: auto;
    height: 100%;

    img {
      height: 100%;
    }
  }

  &__delete-button {
    position: absolute;
    top: $gutter;
    right: $gutter;
    padding: 0.5rem;
    font-size: 1rem;
    background: $--color-danger !important;
    color: #fff !important;
    border-width: 0;

    @include rtl() {
      right: auto;
      left: $gutter;
    }
  }

  &__dragging-item {
    #{$block}__delete-button {
      display: none;
    }
  }

  &__ghost-item {
    opacity: 0.3;

    #{$block}__delete-button {
      display: none;
    }
  }
}
</style>
